import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { AuthorizationService, CoreService, LoggingService, PortalService, SecurityService } from 'src/app/services';
import { IPortalConfig } from 'src/app/models';
import { Helpers } from 'src/app/helpers';
import { Constants } from '../../models/constants';
import { environment } from 'src/environments/environment';
import { CitationService } from 'src/app/services/citation.service';
import MarkdownIt from 'markdown-it';
import sanitizeHtml from 'sanitize-html';

declare var window: any;

@Component({
  selector: 'medpt-document-viewer-ai-chat',
  templateUrl: './document-viewer-ai-chat.component.html',
  styleUrls: ['./document-viewer-ai-chat.component.scss'],
  standalone: false
})
export class DocumentViewerAiChatComponent implements OnInit, OnChanges, OnDestroy {
  @Input() documentContext: any;
  @Output() chatAvailabilityChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() chatOpenStateChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() citationNavigate: EventEmitter<{ documentId: string; pageNumber: number; searchText: string; Source: string }> = new EventEmitter();
  @ViewChild('documentViewerChatHost', { static: false }) public chatHost?: ElementRef;

  portalInfo: IPortalConfig;
  isChatEnabled = false;
  isChatOpen = false;

  private chatTokenData: any;
  private directLine: any;
  private webChatStore: any;
  private hasRenderedChat = false;
  private hasSentConversationStart = false;
  private startedConversationId: string | null = null;
  private chatUser: any;
  private chatUserId = '';
  private removeChatClickListener?: () => void;
  private disclaimerIntervalId?: ReturnType<typeof setInterval>;
  private markdownRenderer?: MarkdownIt;
  isUserReviewer:boolean = false;
  statusMessage: string = '';
  statusStage: string = '';
  isWaitingForResponse: boolean = false;
  private isFeedbackPending: boolean = false;

  sanitizeOptions = {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['a']),
    allowedAttributes: {
      a: ['href', 'target', 'rel', 'title', 'data-cite-href'],
    },
    allowedSchemes: ['http', 'https', 'ftp', 'mailto'],
    allowSchemeRelative: true,
    transformTags: {
      'a': (tagName: string, attribs: any) => {
        const href = attribs.href || '';
        if (href.startsWith('cite:') || (href && !href.startsWith('http') && !href.startsWith('/') && !href.startsWith('#') && !href.startsWith('mailto:'))) {
          return {
            tagName: 'a',
            attribs: {
              ...attribs,
              'data-cite-href': href,
              'href': '#'
            }
          };
        }
        return { tagName, attribs };
      }
    },
    exclusiveFilter: (frame: any) => {
      return (
        frame.tag === 'img' &&
        frame.attribs.class?.includes('webchat__render-markdown__external-link-icon')
      );
    },
  };

  constructor(
    private portalService: PortalService,
    private securityService: SecurityService,
    private coreService: CoreService,
    private authorizationService: AuthorizationService,
    private loggingService: LoggingService,
    private citationService: CitationService,
    private renderer: Renderer2
  ) {
    this.portalInfo = this.portalService.portalConfiguation.getValue();
    this.isUserReviewer = this.authorizationService.isNavigationOrFeatueAllowed(Constants.ASSIGN_REVIEWER_FEATURE_NAME);  
  }

  ngOnInit(): void {
    this.initializeDocumentChat();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentContext'] && this.hasRenderedChat) {
      this.sendDocumentContextToChat();
    }
  }

  ngAfterViewChecked(): void {
    if (this.isChatEnabled && this.isChatOpen && !this.hasRenderedChat) {
      this.tryRenderDocumentChat();
    }
  }

  ngOnDestroy(): void {
    this.removeChatClickListener?.();
    this.removeChatClickListener = undefined;
    if (this.disclaimerIntervalId) {
      clearInterval(this.disclaimerIntervalId);
      this.disclaimerIntervalId = undefined;
    }
    this.endChatRuntime();
  }

  openChatPanel(): void {
    this.isChatOpen = true;
    this.chatOpenStateChange.emit(true);
    setTimeout(() => {
      this.tryRenderDocumentChat();
    });
  }

  closeChatPanel(): void {
    this.isChatOpen = false;
    this.chatOpenStateChange.emit(false);
    this.endChatRuntime();
  }

  private endChatRuntime(): void {
    if (this.directLine?.end) {
      this.directLine.end();
    }
    this.directLine = null;
    this.webChatStore = null;
    this.hasRenderedChat = false;
    this.hasSentConversationStart = false;
    this.hideStatusIndicator();
    this.isWaitingForResponse = false;
    this.removeChatClickListener?.();
    this.removeChatClickListener = undefined;

    if (this.chatHost?.nativeElement) {
      this.chatHost.nativeElement.innerHTML = '';
    }

    if (this.disclaimerIntervalId) {
      clearInterval(this.disclaimerIntervalId);
      this.disclaimerIntervalId = undefined;
    }
  }

  private async initializeDocumentChat(): Promise<void> {
    if (!this.authorizationService.isNavigationOrFeatueAllowed(Constants.RASPberryAI_FEATURE_NAME)) {
      this.isChatEnabled = false;
      this.chatAvailabilityChange.emit(false);
      return;
    }

    if (Helpers.isNullOrUndefined(this.portalInfo) || Helpers.isNullOrUndefined(this.portalInfo.PortalID)) {
      this.isChatEnabled = false;
      this.chatAvailabilityChange.emit(false);
      return;
    }

    const currentUser = this.securityService.currentUserValue;
    if (Helpers.isNullOrUndefined(currentUser)) {
      this.isChatEnabled = false;
      this.chatAvailabilityChange.emit(false);
      return;
    }

    try {
      await this.ensureWebChatScriptsLoaded();
      const tokenResult = await this.coreService.generateWebChatToken(this.portalInfo.PortalID);
      if (!tokenResult?.Success || Helpers.isNullOrUndefined(tokenResult.Data?.Token)) {
        this.isChatEnabled = false;
        this.chatAvailabilityChange.emit(false);
        return;
      }

      this.chatTokenData = tokenResult.Data;
      this.chatUserId = currentUser.UserGuid;
      this.chatUser = {
        UserID: currentUser.UserID,
        Id: currentUser.UserGuid,
        FirstName: currentUser.FirstName,
        LastName: currentUser.LastName,
        Email: currentUser.UserName,
        Name: currentUser.FirstName + ' ' + currentUser.LastName,
        ConversationId: tokenResult.Data.ConversationId
      };
      this.isChatEnabled = true;
      this.chatAvailabilityChange.emit(true);
    } catch (err) {
      this.isChatEnabled = false;
      this.chatAvailabilityChange.emit(false);
      this.loggingService.Error(
        `<MedPoint.WebApp.TriPort.Shared.DocumentViewerAiChatComponent.initializeDocumentChat> Failed with message=${JSON.stringify(err)}`
      );
    }
  }

  private async ensureWebChatScriptsLoaded(): Promise<void> {
    if (window.WebChat) {
      return;
    }
    await this.loadExternalScript('/assets/botchat/botchat-es5.js');
    await this.loadExternalScript('/assets/botchat/webchat-es5.js');
  }

  private loadExternalScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${url}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = url;
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = (err) => reject(err);
      document.body.appendChild(script);
    });
  }

  private tryRenderDocumentChat(): void {
    if (!this.isChatEnabled || this.hasRenderedChat || !this.chatHost || Helpers.isNullOrUndefined(this.chatTokenData?.Token) || !window.WebChat) {
      return;
    }

    this.directLine = window.WebChat.createDirectLine({
      token: this.chatTokenData.Token,
      websocket: true
    });

    this.webChatStore = window.WebChat.createStore(
      {},
      () => (next: (action: any) => any) => (action: any) => {
        if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY') {
          const activity = action.payload?.activity;

          if (activity?.type === 'event' && activity?.name === 'statusUpdate') {
            const val = typeof activity.value === 'string' ? JSON.parse(activity.value) : activity.value;
            const stage = val?.stage || val?.Stage;
            const label = val?.label || val?.Label;

            if (stage === 'DONE') {
              this.hideStatusIndicator();
              this.isWaitingForResponse = false;
              this.toggleSendBox(false);
              return next(action);
            }

            if (label) {
              this.statusStage = stage;
              this.showStatusIndicator(label);
            }

            return next(action);
          }

          if (activity?.type === 'message' && activity?.from?.role === 'bot') {
            const isFeedbackCard = !!activity?.value?.ActionType;

            if (isFeedbackCard) {
              return next(action);
            }

            if (activity.channelData) {
              this.citationService.parseCitations(activity.channelData);
            }

            if (activity?.text) {
              if (this.isFeedbackPending) {
                this.isFeedbackPending = false;
              }
              this.hideStatusIndicator();
              this.isWaitingForResponse = false;
              this.toggleSendBox(false);
            }
          }
        }

        if (action.type === 'DIRECT_LINE/POST_ACTIVITY' && action.payload?.activity) {
          const activity = action.payload.activity;
          const isFeedbackAction = !!activity.value?.ActionType;

          if (this.isWaitingForResponse && activity.type === 'message' && !isFeedbackAction) {
            return next(action);
          }

          if (activity.type === 'message' && !isFeedbackAction) {
            this.statusStage = 'ANALYZING';
            this.showStatusIndicator('Analyzing your question...');
            this.isWaitingForResponse = true;
            this.toggleSendBox(true);
          }

          if (isFeedbackAction) {
            this.isFeedbackPending = true;
          }

          action = {
            ...action,
            payload: {
              ...action.payload,
              activity: {
                ...activity,
                channelData: {
                  ...(activity.channelData ?? {}),
                  SponsorName: this.portalInfo.SponsorName,
                  PortalName: this.portalInfo.PortalName,
                  PortalId: this.portalInfo.PortalID,
                  RoleId: this.portalInfo.RoleId,
                  SiteId: this.portalInfo.SiteId,
                  DocumentId: this.documentContext?.documentId,
                  UserId: this.chatUser.UserID,
                  IsUserReviewer: this.isUserReviewer
                }
              }
            }
          };
        }

        return next(action);
      }
    );

    const styleSet = window.WebChat.createStyleSet({
      backgroundColor: '#FFFFFF',
      primaryFont: "'Fabrikat-Regular', 'DejaVu Sans', sans-serif",
      bubbleMaxWidth: 620,
      botAvatarBackgroundColor: '#C93C66',
      userAvatarBackgroundColor: '#C93C66',
      suggestedActionTextColor: 'black',
      suggestedActionBorderColor: '#C93C66',
      suggestedActionBorderWidth: '1px',
      suggestedActionBackground: '#FFFFFF'
    });
    
    const markdownItRenderer = new MarkdownIt({
      html: true,
      breaks: true,
      linkify: true
    });
   // this.markdownRenderer = markdownItRenderer;

    markdownItRenderer.renderer.rules['link_open'] = (
      tokens: any,
      idx: any,
      options: any,
      env: any,
      self: any
    ) => {
      const href = tokens[idx].attrGet('href') || '';
      if (!href.startsWith('cite:')) {
        tokens[idx].attrSet('target', '_blank');
        tokens[idx].attrSet('rel', 'noopener noreferrer');
      }
      tokens[idx].attrSet('title', 'Click to open this document');
      return self.renderToken(tokens, idx, options);
    };

    window.WebChat.renderWebChat(
      {
        directLine: this.directLine,
        locale: 'en-US',
        styleSet,        
        store: this.webChatStore,
        userID: this.chatUserId,
        user: { id: this.chatUserId, name: this.chatUser.Name },
        sendTypingIndicator: true,
        styleOptions: {
          hideUploadButton: true,
          suggestedActionLayout: 'stacked',
          botAvatarImage:
            environment.cdnEndPoint +
            environment.cdnSharedImagesFolder +
            'AI_Chat_Bot_Red.svg',
          userAvatarInitials: this.chatUser?.Name.match(/(^\S\S?|\s\S)?/g)
            .map((v: string) => v.trim())
            .join('')
            .match(/(^\S|\S$)?/g)
            .join('')
            .toLocaleUpperCase(),
          sendTimeout: 20000
        },
        overrideLocalizedStrings: {
          TEXT_INPUT_PLACEHOLDER: 'Ask a question about this document'
        },
        markdownIt: markdownItRenderer,
        renderMarkdown: (text: string) => {
          return sanitizeHtml(markdownItRenderer.render(text), this.sanitizeOptions);
        }
      },
      this.chatHost.nativeElement
    );

    this.registerCitationClickHandler();
    this.addDisclaimer();

    this.directLine.connectionStatus$.subscribe((status: number) => {
      if (status === 2 && !this.hasSentConversationStart) {
        const conversationId = this.chatUser?.ConversationId as string | undefined;

        // startConversation should be sent once per conversation id.
        if (!Helpers.isNullOrUndefined(conversationId) && this.startedConversationId === conversationId) {
          this.hasSentConversationStart = true;
          return;
        }

        this.hasSentConversationStart = true;
        this.startedConversationId = conversationId ?? null;
      }
    });

    this.hasRenderedChat = true;
    this.sendDocumentContextToChat();
  }

  private sendDocumentContextToChat(): void {
    if (!this.hasRenderedChat || !this.directLine || !this.chatUser) {
      return;
    }

    this.directLine
      .postActivity({
        type: 'event',
        name: 'documentContext',
        from: { id: this.chatUserId, name: this.chatUser.Name },
        value: this.documentContext,
        channelData: {
          SponsorName: this.portalInfo.SponsorName,
          PortalName: this.portalInfo.PortalName,
          PortalId: this.portalInfo.PortalID,
          RoleId: this.portalInfo.RoleId,
          SiteId: this.portalInfo.SiteId,
          UserId: this.chatUser.UserID,
          DocumentId: this.documentContext?.documentId,
          IsUserReviewer: this.isUserReviewer
        }
      })
      .subscribe(() => {}, () => {});
  }

  private registerCitationClickHandler(): void {
    this.removeChatClickListener?.();
    this.removeChatClickListener = undefined;

    const chatContainer = this.chatHost?.nativeElement as HTMLElement | undefined;
    if (!chatContainer) {
      return;
    }

    this.removeChatClickListener = this.renderer.listen(chatContainer, 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const anchor = target?.closest('a') as HTMLAnchorElement | null;
      if (!anchor) {
        return;
      }

      const rawHref = anchor.getAttribute('data-cite-href') || anchor.getAttribute('href') || '';
      const decodedHref = rawHref ? decodeURIComponent(rawHref) : '';
      const citeHref = decodedHref.startsWith('cite:') ? decodedHref : rawHref.startsWith('cite:') ? rawHref : '';
      if (!citeHref) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const decoded = decodeURIComponent(citeHref.replace('cite:', ''));
      const refIds = decoded.split('|');
      const chunks = this.citationService.getChunksByRefIds(refIds);
      if (!chunks.length) {
        return;
      }

      const chunk = chunks[0];
      this.citationNavigate.emit({
        documentId: chunk.documentId,
        pageNumber: chunk.pageNumber,
        searchText: chunk.searchText,
        Source: chunk.Source
      });
    });
  }

  private showStatusIndicator(message: string): void {
    this.statusMessage = message;
  }

  private hideStatusIndicator(): void {
    this.statusMessage = '';
    this.statusStage = '';
  }

  private toggleSendBox(disabled: boolean): void {
    const wrapper = this.chatHost?.nativeElement?.closest('.webchat-wrapper');
    if (!wrapper) {
      return;
    }

    if (disabled) {
      wrapper.classList.add('rai-waiting');
    } else {
      wrapper.classList.remove('rai-waiting');
    }
  }

  private addDisclaimer(): void {
    if (this.disclaimerIntervalId) {
      clearInterval(this.disclaimerIntervalId);
      this.disclaimerIntervalId = undefined;
    }

    this.disclaimerIntervalId = setInterval(() => {
      const host = this.chatHost?.nativeElement as HTMLElement | undefined;
      const sendBox = host?.querySelector('.webchat__send-box');
      const existingDisclaimer = host?.querySelector('.custom-disclaimer');

      if (sendBox && !existingDisclaimer) {
        const disclaimer = this.renderer.createElement('div');
        this.renderer.addClass(disclaimer, 'custom-disclaimer');
        this.renderer.setProperty(
          disclaimer,
          'textContent',
          'Generated by AI. Please check reference material links for complete information.'
        );
        this.renderer.appendChild(sendBox, disclaimer);
      }

      if (sendBox) {
        clearInterval(this.disclaimerIntervalId);
        this.disclaimerIntervalId = undefined;
      }
    }, 500);
  }
}
